import type { WeeklyPeriod } from "@swivl-mono/types";

import { mergePeriods } from "./mergePeriods";

/**
 * Best-effort parser for freeform hours strings (the legacy
 * `CortexFacilityOfficeHours` / `AccessHours` / etc. fields). Returns a
 * `WeeklyPeriod[]` when at least one rule was understood, otherwise null.
 *
 * **Intentional non-goal**: be perfect. Operators write all kinds of weirdness
 * ("M-F 9 AM - 5:30 PM", "Mon-Fri: 9am to 5:30pm", "9-5 weekdays, closed
 * weekends", "By appointment only", "24/7"). This parser handles the
 * 80% case + obvious aliases and lets the operator review the result. When
 * we miss, the empty-state UI falls back to letting them enter periods
 * manually.
 *
 * Recognized patterns (loose, case-insensitive, mixed punctuation OK):
 * - "Mon-Fri 9 AM - 5:30 PM"  → weekdays 9:00–17:30
 * - "Mon - Fri: 9:00 AM to 5:30 PM" → same
 * - "M-F 9-5"                  → weekdays 9:00–17:00
 * - "Saturday 9-4"             → Sat 9:00–16:00
 * - "Sat-Sun closed"           → no entries
 * - "Daily 6am-10pm"           → all 7 days 6:00–22:00
 * - "24/7" / "24 hours" / "Open 24 hours"  → all 7 days 0:00–24:00
 * - Multiple clauses separated by `,` `;` `\n` or ` and `
 *
 * Not handled (returns null or partial result):
 * - "By appointment only"
 * - Date-scoped rules ("until further notice", "starting Aug 5")
 * - Time-zone qualifiers in the string (we ignore them)
 * - Holiday clauses (those go in `HolidayRule`, not the weekly grid)
 */

const DAY_ALIASES: Record<string, number> = {
  // 0=Sun..6=Sat (matches WeeklyPeriod.dayOfWeek)
  sun: 0,
  sunday: 0,
  mon: 1,
  monday: 1,
  m: 1,
  tue: 2,
  tues: 2,
  tuesday: 2,
  t: 2,
  wed: 3,
  weds: 3,
  wednesday: 3,
  w: 3,
  thu: 4,
  thur: 4,
  thurs: 4,
  thursday: 4,
  th: 4,
  fri: 5,
  friday: 5,
  f: 5,
  sat: 6,
  saturday: 6,
  s: 6
};

const ORDERED_WEEK = [0, 1, 2, 3, 4, 5, 6];

function expandDayRange(start: number, end: number): number[] {
  // Cyclic across week boundary if needed (rare; "Sat-Mon" → Sat, Sun, Mon).
  const out: number[] = [];
  let cur = start;
  while (true) {
    out.push(cur);
    if (cur === end) break;
    cur = (cur + 1) % 7;
    if (out.length > 7) break;
  }
  return out;
}

function dayFromToken(tok: string): number | null {
  const k = tok.trim().toLowerCase().replace(/\.$/, "");
  if (k in DAY_ALIASES) return DAY_ALIASES[k]!;
  // Plurals: "Saturdays", "Sundays". Only full names, so a bare "s" or "ts" can't turn into a day.
  const singular = k.endsWith("s") ? k.slice(0, -1) : k;
  if (singular.length >= 6 && singular in DAY_ALIASES) return DAY_ALIASES[singular]!;
  return null;
}

function parseClock(s: string): number | null {
  // Accept "9", "9am", "9 AM", "9:30", "9:30am", "17:30", "0900"
  const cleaned = s.trim().toLowerCase().replace(/\./g, "");
  // 0900 / 1730 (military, no colon, no am/pm)
  const military = cleaned.match(/^([01]?\d|2[0-3])([0-5]\d)$/);
  if (military && cleaned.length === 4) {
    return parseInt(military[1]!, 10) * 60 + parseInt(military[2]!, 10);
  }
  // "6a" / "9p" shorthand is read as am / pm.
  const m = cleaned.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm|a|p)?$/);
  if (!m) return null;
  let h = parseInt(m[1]!, 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const meridiem = m[3] ? (m[3].startsWith("a") ? "am" : "pm") : undefined;
  if (h < 0 || h > 24) return null;
  if (min < 0 || min > 59) return null;
  if (meridiem === "am") {
    if (h === 12) h = 0;
  } else if (meridiem === "pm") {
    if (h !== 12) h += 12;
  }
  return h * 60 + min;
}

/** One classified clause: a time period (days null = none given), a closed-days marker, or a day-only list. */
type ClauseResult =
  | { kind: "period"; days: number[] | null; openMinute: number; closeMinute: number }
  | { kind: "closed"; days: number[] }
  | { kind: "dayonly"; days: number[] };

/** A time's am/pm marker, including the "6a" / "9p" shorthand. */
const MERIDIEM = /\d\s*(?:am|pm|a|p)\b/i;

/** One time range ("9am-5pm", "9:00 AM to 5:30 PM", "6a-9p", "0900-1730"). Global copy for counting. */
const TIME_RANGE_SOURCE =
  "(\\d{1,2}(?::\\d{2})?\\s*(?:am|pm|a|p)?(?![a-z])|\\d{4})\\s*(?:-|–|—|to)\\s*(\\d{1,2}(?::\\d{2})?\\s*(?:am|pm|a|p)?(?![a-z])|\\d{4})";

/** When the close is <= the open, read it as PM ("9-5" = 09:00–17:00) unless an explicit am/pm marks a genuine overnight span ("10pm-6am"), which rolls to the next day. */
function resolveCloseMinute(openStr: string, closeStr: string, open: number, close: number): number {
  if (close > open) return close;
  const hasMeridiem = MERIDIEM.test(openStr) || MERIDIEM.test(closeStr);
  if (!hasMeridiem && close + 12 * 60 > open) return close + 12 * 60;
  return close + 24 * 60;
}

/** Strip common timezone tokens (EST, PDT, UTC…) so they aren't mistaken for day text. */
function stripTimezoneNoise(s: string): string {
  return s.replace(/\b([ecmp][sd]?t|utc|gmt)\b/gi, " ").trim();
}

function classifyClause(raw: string): ClauseResult | null {
  const text = raw.trim();
  if (!text) return null;

  if (/24\s*\/\s*7|24\s*hours?|24\s*hrs?\b|always\s+open|all\s+day/i.test(text) && !/closed/i.test(text)) {
    return { kind: "period", days: [...ORDERED_WEEK], openMinute: 0, closeMinute: 24 * 60 };
  }

  // "X closed" → the days to remove later. No days ("Sun, closed" splits into two clauses) → the
  // closed marker applies to the day clause next to it.
  if (/closed/i.test(text)) {
    const rest = text
      .replace(/closed|\bon\b/gi, " ")
      .replace(/[\s:.,\-–—]+/g, " ")
      .trim();
    if (!rest) return { kind: "closed", days: [] };
    const closedDays = parseDayPart(rest);
    return closedDays && closedDays.length > 0 ? { kind: "closed", days: closedDays } : null;
  }

  const timeMatch = text.match(new RegExp(TIME_RANGE_SOURCE, "i"));
  if (!timeMatch) {
    // No time — a day-only clause like "Mon-Fri" that a sibling time clause can use.
    const only = parseDayPart(text);
    return only && only.length > 0 ? { kind: "dayonly", days: only } : null;
  }

  // Days may appear before or after the time; timezone noise is ignored.
  const before = text.slice(0, timeMatch.index ?? 0).trim();
  const after = stripTimezoneNoise(text.slice((timeMatch.index ?? 0) + timeMatch[0].length));
  const days = parseDayPart(before) ?? parseDayPart(after);

  const open = parseClock(timeMatch[1]!);
  const close = parseClock(timeMatch[2]!);
  if (open === null || close === null) return null;
  const closeMin = resolveCloseMinute(timeMatch[1]!, timeMatch[2]!, open, close);

  return { kind: "period", days: days && days.length > 0 ? days : null, openMinute: open, closeMinute: closeMin };
}

function parseDayPart(part: string): number[] | null {
  // "Mon - Sat - 9AM" leaves "Mon - Sat -"; "Mon thru Fri" / "Mon to Sat" are ranges.
  const trimmed = part
    .replace(/[:.,\-–—]+\s*$/, "")
    .replace(/\s+(?:thru|through|to)\s+/gi, "-")
    .trim();
  if (!trimmed) return null;

  // "Daily" / "Everyday" / "All days" / "All week" / "7 days (a week)" / "7 day's a week"
  if (/^(daily|every\s*day|all\s*days|all\s*week|7\s*day'?s?(\s*a\s*week)?)$/i.test(trimmed)) return [...ORDERED_WEEK];

  // "Weekdays" / "Mon-Fri shorthand"
  if (/^weekdays?$/i.test(trimmed)) return [1, 2, 3, 4, 5];
  if (/^weekends?$/i.test(trimmed)) return [0, 6];

  // Range: "Mon-Fri", "M - F", "Tuesday — Sunday".
  const rangeMatch = trimmed.match(/^([a-z]+)\s*[-–—]\s*([a-z]+)$/i);
  if (rangeMatch) {
    const start = dayFromToken(rangeMatch[1]!);
    const end = dayFromToken(rangeMatch[2]!);
    if (start === null || end === null) return null;
    return expandDayRange(start, end);
  }

  // A list whose items are days or ranges: "Mon, Wed, Fri", "MON-TUE,THU-SAT", "Wed/Sun", "Sun & Mon".
  const days: number[] = [];
  const items = trimmed
    .split(/[,&/]|\s+and\s+/i)
    .map((t) => t.trim())
    .filter(Boolean);
  for (const item of items) {
    const range = item.match(/^([a-z]+)\s*[-–—]\s*([a-z]+)$/i);
    const itemDays: (number | null)[] = range
      ? (() => {
          const start = dayFromToken(range[1]!);
          const end = dayFromToken(range[2]!);
          return start === null || end === null ? [null] : expandDayRange(start, end);
        })()
      : item.split(/\s+/).map(dayFromToken);
    for (const d of itemDays) {
      if (d === null) return null;
      if (!days.includes(d)) days.push(d);
    }
  }
  return days.length > 0 ? days : null;
}

/**
 * Strip common label prefixes operators put in front of the actual hours.
 * Examples:
 *   "Customer Service Hours: M-F 9 AM - 5:30 PM" → "M-F 9 AM - 5:30 PM"
 *   "Office hours: 9-5"                          → "9-5"
 *   "Typically our office hours are T-F ..."     → "T-F ..."
 */
function stripLabelPrefix(text: string): string {
  const patterns = [
    /^(typically|normally|usually|generally)\s+(our\s+|the\s+)?[a-z]*\s*hours?\s+(are|is)\s*[:\-—]?\s*/i,
    /^(our\s+|the\s+)?(office|business|customer\s+service|store|facility|operating|access|gate|call\s*center)\s+hours?\s*[:\-—]?\s*/i,
    /^hours?\s*(of\s+operation)?\s*[:\-—]\s*/i
  ];
  let out = text;
  for (const p of patterns) out = out.replace(p, "");
  return out.trim();
}

/**
 * Insert a comma between a closing time and a following day token so missing-
 * separator inputs ("Tu-F 9 AM - 5:30 PM SAT 9 AM - 4 PM") split into clauses.
 */
function insertMissingSeparators(text: string): string {
  // Closing-time pattern (am/pm or 4-digit military) followed by whitespace
  // and a recognized day token → insert a ", " separator.
  const re =
    /((?:\d{1,2}(?::\d{2})?\s*(?:am|pm|a|p)\b|\d{4}))\s+(?=(?:sun|mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|daily|every\s*day|weekdays?|weekends?|sundays?|mondays?|tuesdays?|wednesdays?|thursdays?|fridays?|saturdays?)\b)/gi;
  // Two passes catch overlapping/back-to-back day groups. A time followed by "Closed" ("9am-3pm
  // Closed Sunday") also starts a new clause, so the closure isn't read into the time clause.
  return text
    .replace(re, "$1, ")
    .replace(re, "$1, ")
    .replace(/((?:\d{1,2}(?::\d{2})?\s*(?:am|pm|a|p)\b|\d{4}))\s+(?=closed\b)/gi, "$1, ");
}

/** Structured parse result — the weekly grid plus whether days had to be assumed. */
export interface ParsedFreeformHours {
  periods: WeeklyPeriod[];
  /** True when a clause gave a time but no day, so all 7 days were assumed (worth operator review). */
  assumedAllDays: boolean;
  /**
   * False when the parse may not say what the text says: part of the text wasn't understood, a time
   * range or a named day in it went unused, or a day got two different openings. A caller that
   * writes the result without review (the Cortex migrator) must not trust an unconfident parse.
   */
  confident: boolean;
}

/**
 * Legacy hours store line breaks as a literal backslash-n (two characters) or `<br>`, write "a.m.",
 * and separate day blocks with " / ". Normalize those so the clause split sees them.
 */
function normalizeHoursText(text: string): string {
  return text
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\b([ap])\.m\.?/gi, "$1m")
    .replace(/\s+\/\s+/g, "; ")
    .replace(/\b(?:thru|through)\b/gi, "-");
}

/** Values that mean "no hours" rather than hours the parser failed to read. */
export function isNoHoursText(input: string | null | undefined): boolean {
  return /^\s*(?:none|n\/?a|no\s+hours|no\s+office(?:\s+hours)?)\s*\.?\s*$/i.test(input ?? "");
}

const DAY_MENTION = /\b(sun|mon|tue|tues|wed|weds|thu|thur|thurs|fri|sat|sundays?|mondays?|tuesdays?|wednesdays?|thursdays?|fridays?|saturdays?)\b/gi;

/** Like {@link parseFreeformHours} but also reports whether days were assumed and whether the parse is trustworthy; null when nothing was understood. */
export function parseFreeformHoursDetailed(input: string | null | undefined): ParsedFreeformHours | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Pre-process: normalize separators, strip label prefixes ("Customer Service Hours:", etc.),
  // insert missing comma separators between adjacent day-clauses.
  const text = insertMissingSeparators(stripLabelPrefix(normalizeHoursText(trimmed)));

  // Split into clauses on `,`, `;`, newlines, or " and ".
  const clauses = text
    .split(/[,;\n]|\s+and\s+/i)
    .map((c) => c.trim())
    .filter(Boolean);

  if (clauses.length === 0) return null;

  // Time-first lists put each time before its days: "9:30 am - 6pm M-F 9am-5pm Saturday". A
  // clause that starts with a time and holds another one splits before each later time, so every
  // time keeps the days that follow it.
  const timeFirst = new RegExp(`^${TIME_RANGE_SOURCE}`, "i");
  const splitClauses = clauses.flatMap((clause) => {
    if (!timeFirst.test(clause)) return [clause];
    const starts = [...clause.matchAll(new RegExp(TIME_RANGE_SOURCE, "gi"))].map((m) => m.index ?? 0);
    if (starts.length < 2) return [clause];
    return starts.map((start, i) => clause.slice(start, starts[i + 1]).trim()).filter(Boolean);
  });

  const classified = splitClauses.map(classifyClause);
  const results = classified.filter((r): r is ClauseResult => r !== null);
  // A lone day list with no time ("Mon-Fri") isn't understood — let it fail so it's flagged.
  if (!results.some((r) => r.kind === "period" || r.kind === "closed")) return null;

  // A clause with no days takes them from the day list beside it: "Mon-Fri, 9am-5pm" and
  // "9am-5pm, Mon-Fri" both pair; "Sun, closed" closes Sunday. Consecutive day lists
  // ("Mon, Wed, 9-5") read as one. Pairing by position, not by pooling every day list in the
  // text, is what keeps "Mon-Fri, 10-6 / Sat, 10-4" from giving Saturday both times.
  const groups: ClauseResult[] = [];
  for (const r of results) {
    const prev = groups[groups.length - 1];
    if (r.kind === "dayonly" && prev?.kind === "dayonly") {
      prev.days = [...new Set([...prev.days, ...r.days])];
      continue;
    }
    // A comma split a day list: "MON-TUE,THU-SAT 9am-5:30pm" or "Thursday and Sunday: Closed". A
    // day list right before a clause that names its own days belongs to that clause, unless a time
    // with no days sits before it: "9-5pm, mon-fri, 10-10 saturday" gives Mon-Fri to the 9-5.
    const beforePrev = groups[groups.length - 2];
    const timeAwaitsDays = beforePrev?.kind === "period" && beforePrev.days === null;
    if (prev?.kind === "dayonly" && !timeAwaitsDays && (r.kind === "closed" || r.kind === "period") && r.days !== null && r.days.length > 0) {
      groups[groups.length - 1] = { ...r, days: [...new Set([...prev.days, ...r.days])] };
      continue;
    }
    groups.push(r.kind === "dayonly" ? { kind: "dayonly", days: [...r.days] } : r);
  }
  const claimed = new Set<number>();
  const adjacentDays = (i: number): number[] | null => {
    for (const j of [i - 1, i + 1]) {
      const g = groups[j];
      if (g?.kind === "dayonly" && !claimed.has(j)) {
        claimed.add(j);
        return g.days;
      }
    }
    return null;
  };

  const periods: WeeklyPeriod[] = [];
  const closedDays = new Set<number>();
  let assumedAllDays = false;
  let usedRanges = 0;
  groups.forEach((g, i) => {
    if (g.kind === "closed") {
      const days = g.days.length > 0 ? g.days : (adjacentDays(i) ?? []);
      for (const d of days) closedDays.add(d);
      return;
    }
    if (g.kind !== "period") return;
    usedRanges++;
    let days = g.days ?? adjacentDays(i);
    if (!days) {
      // No day given for this time: assume every day (flagged).
      days = [...ORDERED_WEEK];
      assumedAllDays = true;
    }
    for (const d of days) {
      periods.push({ dayOfWeek: d as WeeklyPeriod["dayOfWeek"], openMinute: g.openMinute, closeMinute: g.closeMinute });
    }
  });

  // Apply closures after periods are built.
  for (let i = periods.length - 1; i >= 0; i--) {
    if (closedDays.has(periods[i]!.dayOfWeek)) periods.splice(i, 1);
  }

  // Collapse overlapping or back-to-back windows per day ("Mon-Fri 9-5, Mon 2-6"
  // is Monday 9-6). Open times are unchanged, and the result can be saved: the
  // server rejects overlapping windows (ENG-3316). Sorted for stable output.
  const merged = ([0, 1, 2, 3, 4, 5, 6] as const).flatMap((d) =>
    mergePeriods(periods.filter((p) => p.dayOfWeek === d)).map((p) => ({ dayOfWeek: d, ...p }))
  );

  // Confidence: everything the text says must be in the result. Each failure below was a real
  // production value that used to be written wrong without any flag. Judged on the PRE-merge
  // periods: merging is a tidy-up, and collapsing two windows would hide that the text said
  // two different things ("24 Hour Access ... Call Center Hours: 8AM - 8PM").
  const openDays = new Set<number>(periods.map((p) => p.dayOfWeek));
  const mentionedDays = [...text.matchAll(DAY_MENTION)].map((m) => dayFromToken(m[1]!)).filter((d): d is number => d !== null);
  const rangesInText = [...text.matchAll(new RegExp(TIME_RANGE_SOURCE, "gi"))].length;
  const is247 = /24\s*\/\s*7|24\s*hours?|24\s*hrs?\b|always\s+open|all\s+day/i.test(text);
  const confident =
    !classified.includes(null) &&
    openDays.size === periods.length &&
    (is247 || usedRanges >= rangesInText) &&
    mentionedDays.every((d) => openDays.has(d) || closedDays.has(d)) &&
    !groups.some((g, i) => g.kind === "dayonly" && !claimed.has(i));

  return { periods: merged, assumedAllDays, confident };
}

/** Parses a freeform hours string into a `WeeklyPeriod[]` (null = nothing understood; `[]` = closed all week). */
export function parseFreeformHours(input: string | null | undefined): WeeklyPeriod[] | null {
  const result = parseFreeformHoursDetailed(input);
  return result ? result.periods : null;
}
